import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, User, LogOut, LayoutDashboard, Heart, GitCompare, Globe } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useWishlist } from "../context/WishlistContext";
import { useCompare } from "../context/CompareContext";
import { useSiteSettings } from "../context/SiteSettingsContext";
import { useLang } from "../context/LanguageContext";

export default function Header() {
  const { count } = useCart();
  const { count: wlCount } = useWishlist();
  const { count: cmpCount } = useCompare();
  const { user, logout } = useAuth();
  const { settings } = useSiteSettings() || {};
  const { lang, setLang, t, tBrand } = useLang();
  const navigate = useNavigate();
  const logo = settings?.general?.logo_url;
  const siteName = tBrand(settings?.general?.site_name || "SHAMSTRADE");

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 border-b border-black/10 bg-white/85 backdrop-blur-xl"
    >
      <div className="max-w-[1400px] mx-auto px-3 sm:px-6 md:px-12 h-16 flex items-center justify-between gap-2">
        <Link
          to="/"
          data-testid="logo-link"
          className="flex items-center gap-2 group shrink-0 min-w-0"
        >
          {logo && (
            <img
              src={logo}
              alt={siteName}
              className="w-8 h-8 sm:w-9 sm:h-9 object-contain shrink-0"
            />
          )}
          <span className="font-display font-black text-base sm:text-xl tracking-tight truncate max-w-[120px] sm:max-w-none">
            {siteName}
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
          <Link to="/shop" data-testid="nav-shop" className="hover:text-electric transition-colors">
            {t("nav.shop")}
          </Link>
          <Link to="/shop?type=split" data-testid="nav-split" className="hover:text-electric transition-colors">
            {t("nav.split")}
          </Link>
          <Link to="/shop?type=cassette" data-testid="nav-cassette" className="hover:text-electric transition-colors">
            {t("nav.cassette")}
          </Link>
          <Link to="/shop?type=floor" data-testid="nav-floor" className="hover:text-electric transition-colors">
            {t("nav.floor")}
          </Link>
          <Link to="/shop?type=duct" data-testid="nav-duct" className="hover:text-electric transition-colors">
            {t("nav.duct")}
          </Link>
          <Link to="/about" data-testid="nav-about" className="hover:text-electric transition-colors">
            {t("nav.about")}
          </Link>
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
          {/* Language toggle */}
          <button
            type="button"
            onClick={() => setLang(lang === "ar" ? "en" : "ar")}
            data-testid="lang-toggle"
            aria-label="Switch language"
            className="inline-flex items-center gap-1 sm:gap-1.5 h-9 sm:h-10 px-2.5 sm:px-3 rounded-full bg-secondary hover:bg-ink hover:text-white transition-colors text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.18em] whitespace-nowrap"
          >
            <Globe size={12} />
            <span data-testid="lang-current">{lang === "ar" ? "EN" : "AR"}</span>
          </button>

          {cmpCount > 0 && (
            <Link
              to="/compare"
              data-testid="nav-compare"
              className="hidden sm:inline-flex relative items-center justify-center w-10 h-10 rounded-full bg-secondary hover:bg-ink hover:text-white transition-colors"
              aria-label={t("nav.compare")}
            >
              <GitCompare size={16} />
              <span data-testid="compare-count" className="absolute -top-1 -right-1 bg-electric text-white text-[10px] font-mono font-bold w-5 h-5 grid place-items-center rounded-full">
                {cmpCount}
              </span>
            </Link>
          )}
          <Link
            to="/wishlist"
            data-testid="nav-wishlist"
            className="relative inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-secondary hover:bg-rose-500 hover:text-white transition-colors"
            aria-label={t("nav.wishlist")}
          >
            <Heart size={15} />
            {wlCount > 0 && (
              <span data-testid="wishlist-count" className="absolute -top-1 -right-1 bg-rose-500 text-white text-[10px] font-mono font-bold w-5 h-5 grid place-items-center rounded-full">
                {wlCount}
              </span>
            )}
          </Link>
          <Link
            to="/cart"
            data-testid="nav-cart"
            className="relative inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-secondary hover:bg-ink hover:text-white transition-colors"
            aria-label={t("nav.cart")}
          >
            <ShoppingCart size={15} />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-1 -right-1 bg-electric text-white text-[10px] font-mono font-bold w-5 h-5 grid place-items-center rounded-full"
              >
                {count}
              </span>
            )}
          </Link>

          {user && (user.role === "admin" || user.role === "staff") && (
            <Link
              to="/admin"
              data-testid="nav-admin"
              className="hidden sm:inline-flex items-center gap-2 px-3 h-10 rounded-full bg-secondary hover:bg-ink hover:text-white transition-colors text-xs font-mono uppercase tracking-widest whitespace-nowrap"
            >
              <LayoutDashboard size={14} /> {t("nav.admin")}
            </Link>
          )}

          {user && user !== false ? (
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Link
                to="/account"
                data-testid="nav-account"
                className="hidden sm:inline-flex items-center gap-2 px-3 h-10 rounded-full bg-secondary hover:bg-ink hover:text-white transition-colors text-xs font-mono uppercase tracking-widest whitespace-nowrap"
              >
                <User size={14} /> {user.name?.split(" ")[0] || t("nav.account")}
              </Link>
              <button
                onClick={async () => {
                  await logout();
                  navigate("/");
                }}
                data-testid="nav-logout"
                className="inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-secondary hover:bg-destructive hover:text-white transition-colors"
                aria-label={t("nav.signout")}
              >
                <LogOut size={15} />
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login"
              className="inline-flex items-center justify-center h-9 sm:h-10 px-3 sm:px-4 rounded-full bg-ink text-white hover:bg-electric transition-colors text-[10px] sm:text-xs font-mono uppercase tracking-[0.18em] whitespace-nowrap"
            >
              {t("nav.signin")}
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
